<template lang="pug">
b-modal(
  id="modal-cambiar-boleta-factura"
  :visible="showModal"
  title="Cambiar Boleta a Factura"
  size="md"
  hide-footer
  centered
  :no-close-on-backdrop="true"
  @hide="handleModalClose"
)
  .modal-cambiar-boleta
    //- Resumen Boleta
    .boleta-summary-box.mb-3
      .boleta-summary-title Boleta Nº {{ numeroBoleta }}
      .boleta-summary-subtitle Monto: {{ montoBoleta | formatCurrency }} • Emitida el: {{ fechaBoleta }}

    //- Datos de Facturacion
    h6.datos-facturacion-title.mb-3 Datos de Facturación

    //- RUT
    b-form-group(label="Rut" label-for="rut-input" label-class="form-field-label")
      .rut-input-wrapper
        b-form-input(
          id="rut-input"
          v-model="rutFormateado"
          placeholder="96.876.543-2"
          :state="rutState"
          :disabled="submitting"
          @blur="validarRUT"
          @keyup.enter="validarRUT"
        )
        b-spinner.rut-spinner(v-if="validandoRut" small)
      b-form-invalid-feedback(:state="rutState")
        | {{ rutError }}

    //- Razon Social (Solo lectura)
    b-form-group(label="Razón Social" label-class="form-field-label")
      .readonly-data-text {{ razonSocial || '-' }}

    //- Giro
    b-form-group(label="Giro" label-for="giro-select" label-class="form-field-label")
      b-form-select(
        id="giro-select"
        v-if="opcionesGiro.length > 0"
        v-model="giro"
        :options="opcionesGiro"
        :disabled="submitting || validandoRut"
      )
      b-form-input(
        v-else
        id="giro-input"
        v-model="giro"
        placeholder="Ejemplo de Giro"
        :disabled="submitting || validandoRut"
      )

    //- Aviso de sin direccion tributaria registrada
    b-alert.mb-3(
      v-if="rutValidado && opcionesDireccion.length === 0"
      variant="warning"
      show
    )
      | La empresa no tiene dirección tributaria registrada en el SII

    //- Direccion Tributaria (Desplegable)
    b-form-group(label="Dirección Tributaria" label-for="direccion-select" label-class="form-field-label")
      b-form-select(
        id="direccion-select"
        :value="direccionSeleccionadaActual"
        :options="opcionesDireccion"
        :disabled="submitting || validandoRut || opcionesDireccion.length === 0"
        @change="onDireccionChange"
      )

    //- Comuna Tributaria (Solo lectura)
    b-form-group(label="Comuna Tributaria" label-class="form-field-label")
      .readonly-data-text {{ comuna || '-' }}

    //- Advertencia
    .advertencia-box.mb-4
      b-icon-exclamation-triangle-fill.advertencia-icon.mr-2
      .advertencia-text
        strong Advertencia: 
        | Esta operación no se puede deshacer. Al confirmar, el sistema emitirá 
        strong automáticamente una Nota de Crédito 
        | para anular la boleta actual y generará una nueva 
        strong Factura 
        | por el mismo monto ({{ montoBoleta | formatCurrency }}) a nombre de la empresa indicada.

    //- Botones de Accion
    .modal-actions.d-flex.justify-content-between.align-items-center
      b-button.btn-cancelar(
        variant="outline-secondary"
        @click="closeModal"
        :disabled="submitting"
      ) Cancelar
      b-button.btn-confirmar(
        variant="primary"
        @click="submitCambio"
        :disabled="!formularioValido || submitting"
      )
        b-spinner.mr-2(v-if="submitting" small)
        span Cambiar a factura
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'ModalCambiarBoletaAFactura',
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    pago: {
      type: Object,
      default: () => ({}),
    },
    ov: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      rutRaw: '',
      giro: '',
      direccionSeleccionada: null,
      contribuyenteInfo: null,
      rutError: '',
      validandoRut: false,
      rutValidado: false,
      submitting: false,
    }
  },
  computed: {
    numeroBoleta() {
      return this.pago?.numero_documento || this.pago?.numero_boleta || '-'
    },
    montoBoleta() {
      return this.pago?.monto || 0
    },
    fechaBoleta() {
      const fecha = this.pago?.created_at
      if (!fecha) return '-'
      const d = new Date(fecha)
      if (Number.isNaN(d.getTime())) return '-'
      const dia = d.getDate()
      const meses = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ]
      const mes = meses[d.getMonth()]
      const anio = d.getFullYear()
      return `${dia} de ${mes} de ${anio}`
    },
    rutFormateado: {
      get() {
        return this.rutRaw
      },
      set(val) {
        const rutLimpio = (val || '').replace(/[^0-9kK]/g, '')
        if (rutLimpio.length === 0) {
          this.rutRaw = ''
        } else if (rutLimpio.length === 1) {
          this.rutRaw = rutLimpio
        } else {
          const cuerpo = rutLimpio.slice(0, -1)
          const dv = rutLimpio.slice(-1).toUpperCase()
          const cuerpoFormateado = cuerpo
            .split('')
            .reverse()
            .join('')
            .replace(/(\d{3})(?=\d)/g, '$1.')
            .split('')
            .reverse()
            .join('')
          this.rutRaw = `${cuerpoFormateado}-${dv}`
        }

        if (this.rutValidado) {
          this.rutValidado = false
          this.contribuyenteInfo = null
          this.direccionSeleccionada = null
          this.rutError = ''
        }
      },
    },
    rutState() {
      if (!this.rutRaw) return null
      if (this.validandoRut) return null
      if (this.rutError) return false
      if (this.rutValidado) return true
      return null
    },
    rutLimpio() {
      return (this.rutRaw || '').replace(/\./g, '').trim()
    },
    razonSocial() {
      return this.contribuyenteInfo?.razon_social || ''
    },
    opcionesGiro() {
      const c = this.contribuyenteInfo
      if (!c) return []
      const acts = c.actividades || []
      const vistos = new Set()
      const lista = []
      acts.forEach((a) => {
        const text = a.actividadEconomica || a.giro
        if (text && !vistos.has(text)) {
          vistos.add(text)
          lista.push({ value: text, text })
        }
      })
      if (!lista.length && c.giro) {
        lista.push({ value: c.giro, text: c.giro })
      }
      return lista
    },
    opcionesDireccion() {
      const c = this.contribuyenteInfo
      if (!c) return []
      const vistas = new Set()
      const opciones = []

      const agregarOpcion = (dir, com) => {
        const direccion = (dir || '').trim()
        const comuna = (com || '').trim()
        if (!direccion && !comuna) return
        const key = `${direccion.toUpperCase()}|${comuna.toUpperCase()}`
        if (vistas.has(key)) return
        vistas.add(key)
        const text = comuna ? `${direccion}, ${comuna}` : direccion
        opciones.push({
          text,
          value: { direccion, comuna },
        })
      }

      // 1. Casa Matriz
      agregarOpcion(c.direccion, c.comuna)

      // 2. Sucursales
      const sucursales = c.sucursales || []
      if (Array.isArray(sucursales)) {
        sucursales.forEach((s) => {
          agregarOpcion(s.direccion, s.comuna)
        })
      }

      return opciones
    },
    direccionSeleccionadaActual() {
      return (
        this.direccionSeleccionada || this.opcionesDireccion[0]?.value || null
      )
    },
    direccion() {
      return this.direccionSeleccionadaActual?.direccion || ''
    },
    comuna() {
      return this.direccionSeleccionadaActual?.comuna || ''
    },
    formularioValido() {
      return (
        this.rutValidado &&
        !!this.razonSocial &&
        !!this.giro &&
        !!this.direccion &&
        !!this.comuna &&
        this.opcionesDireccion.length > 0 &&
        !this.submitting &&
        !!this.pago?.id
      )
    },
  },
  watch: {
    showModal(val) {
      if (val) {
        this.resetForm()
      }
    },
  },
  methods: {
    ...mapActions('OVStore', [
      'consultarContribuyente',
      'cambiarBoletaAFactura',
    ]),
    onDireccionChange(val) {
      this.direccionSeleccionada = val
    },
    resetForm() {
      this.rutRaw = ''
      this.giro = ''
      this.direccionSeleccionada = null
      this.contribuyenteInfo = null
      this.rutError = ''
      this.validandoRut = false
      this.rutValidado = false
      this.submitting = false
    },
    async validarRUT() {
      const rut = this.rutLimpio
      if (!rut || rut.length < 3) {
        this.rutError = 'El RUT es requerido'
        this.rutValidado = false
        this.contribuyenteInfo = null
        this.direccionSeleccionada = null
        return false
      }

      this.rutError = ''
      this.contribuyenteInfo = null
      this.direccionSeleccionada = null
      this.validandoRut = true
      this.rutValidado = false

      try {
        const data = await this.consultarContribuyente(rut)
        if (data && data.valido) {
          this.contribuyenteInfo = data
          this.rutValidado = true

          const acts = data.actividades || []
          const principal = acts.find((a) => a.actividadPrincipal) || acts[0]
          this.giro =
            (principal && (principal.actividadEconomica || principal.giro)) ||
            data.giro ||
            ''

          if (this.opcionesDireccion.length > 0) {
            this.direccionSeleccionada = this.opcionesDireccion[0].value
          } else {
            this.direccionSeleccionada = null
          }
          return true
        }

        this.rutError = data?.error || 'RUT no válido o sin razón social'
        this.rutValidado = false
        return false
      } catch (err) {
        this.rutError =
          err.response?.data?.error ||
          'RUT no encontrado en OpenFactura o no registrado como empresa.'
        this.rutValidado = false
        return false
      } finally {
        this.validandoRut = false
      }
    },
    async submitCambio() {
      if (!this.formularioValido) return

      this.submitting = true
      try {
        const payload = {
          pago_id: this.pago.id,
          rut_contribuyente: this.rutLimpio,
          razon_social: this.razonSocial,
          giro: this.giro,
          direccion: this.direccion,
          comuna: this.comuna,
        }

        const res = await this.cambiarBoletaAFactura(payload)

        this.$bvToast.toast(
          `Factura emitida exitosamente (Folio ${res.factura_folio}). Se emitió Nota de Crédito (Folio ${res.nota_credito_folio}).`,
          {
            title: 'Cambio a Factura Exitoso',
            variant: 'success',
            solid: true,
            autoHideDelay: 6000,
          }
        )

        this.$emit('cambiado')
        this.closeModal()
      } catch (err) {
        const errorMsg =
          err.response?.data?.error ||
          'Ocurrió un error al procesar el cambio de boleta a factura.'
        this.$bvToast.toast(errorMsg, {
          title: 'Error al cambiar a Factura',
          variant: 'danger',
          solid: true,
          autoHideDelay: 7000,
        })
      } finally {
        this.submitting = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
    handleModalClose() {
      this.closeModal()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

.modal-cambiar-boleta {
  padding: 0.25rem 0.5rem;
}

.boleta-summary-box {
  background-color: #f0f2f5;
  border-radius: 8px;
  padding: 14px 16px;

  .boleta-summary-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #23201c;
  }

  .boleta-summary-subtitle {
    font-size: 0.85rem;
    color: #6c757d;
    margin-top: 4px;
  }
}

.datos-facturacion-title {
  font-size: 1rem;
  font-weight: 700;
  color: #23201c;
}

::v-deep .form-field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #23201c;
  margin-bottom: 4px;
}

.readonly-data-text {
  font-size: 0.9rem;
  color: #23201c;
  background-color: #f8f9fa;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 6px 12px;
  min-height: 38px;
  display: flex;
  align-items: center;
}

.rut-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;

  .rut-spinner {
    position: absolute;
    right: 12px;
    color: #6c757d;
  }
}

.advertencia-box {
  display: flex;
  align-items: flex-start;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 12px 14px;

  .advertencia-icon {
    font-size: 1.35rem;
    color: #faad14;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .advertencia-text {
    font-size: 0.82rem;
    color: #23201c;
    line-height: 1.45;
  }
}

.modal-actions {
  .btn-cancelar {
    background-color: #fff;
    border: 1px solid #d9d9d9;
    color: #595959;
    font-weight: 500;
    padding: 8px 24px;
    border-radius: 6px;

    &:hover:not(:disabled) {
      background-color: #f5f5f5;
      color: #262626;
      border-color: #bfbfbf;
    }
  }

  .btn-confirmar {
    background-color: #fbb034;
    border-color: #fbb034;
    color: #23201c;
    font-weight: 600;
    padding: 8px 24px;
    border-radius: 6px;

    &:hover:not(:disabled) {
      background-color: #f3a323;
      border-color: #f3a323;
      color: #23201c;
    }

    &:disabled {
      background-color: #ffd591;
      border-color: #ffd591;
      color: #8c8c8c;
      cursor: not-allowed;
    }
  }
}
</style>
